import { useState } from 'react';
import { toast } from 'react-toastify';
import { geocodeAddress } from '../services/geocode';

export const BUILDING_TYPES = ['Commercial', 'Office', 'Residential', 'Industrial', 'Mixed Use'];

export const BADGE_OPTIONS = [
  { label: 'Excellent', color: '#4caf50' },
  { label: 'Good',      color: '#7FB069' },
  { label: 'Fair',      color: '#e1a547' },
  { label: 'Poor',      color: '#d26753' },
];

const initialFormData = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  country: '',
  type: 'Commercial',
  year_built: '',
  total_area: '',
  total_area_unit: 'm2',
  number_of_floors: '',
  occupancy: '',
  performance: '',
  badge: 'Good',
  badge_color: '#7FB069',
  latitude: '',
  longitude: '',
};

const normalizeField = (value) => (value === '' ? null : value);

const buildPayload = (form) => ({
  name: form.name.trim(),
  address: form.address.trim(),
  city: form.city.trim(),
  state: form.state.trim(),
  zip_code: form.zip_code.trim(),
  country: form.country.trim(),
  building_type: form.type,
  year_built: normalizeField(form.year_built),
  total_area: normalizeField(form.total_area),
  total_area_unit: form.total_area_unit,
  number_of_floors: normalizeField(form.number_of_floors),
  occupancy: normalizeField(form.occupancy),
  performance: normalizeField(form.performance),
  badge: form.badge,
  badge_color: form.badge_color,
  latitude: normalizeField(form.latitude),
  longitude: normalizeField(form.longitude),
});

const formFromBuilding = (building) => ({
  name: building.name || '',
  address: building.address || building.location || '',
  city: building.city || '',
  state: building.state || '',
  zip_code: building.zip_code || '',
  country: building.country || '',
  type: building.building_type || building.type || 'Commercial',
  year_built: building.year_built || '',
  total_area: building.total_area || '',
  total_area_unit: building.total_area_unit || 'm2',
  number_of_floors: building.number_of_floors || '',
  occupancy: building.occupancy || '',
  performance: building.performance || '',
  badge: building.badge || 'Good',
  badge_color: building.badge_color || '#7FB069',
  latitude: building.latitude || '',
  longitude: building.longitude || '',
});

/**
 * Add / edit building form state for the Buildings page: opening the modal,
 * field changes, validation, geocoding a missing position and saving through
 * the `addBuilding` / `updateBuilding` actions of useBuildings.
 */
export const useBuildingEditor = ({ addBuilding, updateBuilding }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const openAdd = () => {
    setEditing(null);
    setFormData(initialFormData);
    setOpen(true);
  };

  const openEdit = (building) => {
    setEditing(building);
    setFormData(formFromBuilding(building));
    setOpen(true);
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setEditing(null);
    setFormData(initialFormData);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'badge') {
      const selected = BADGE_OPTIONS.find((item) => item.label === value);
      setFormData((current) => ({
        ...current,
        badge: value,
        badge_color: selected?.color || current.badge_color,
      }));
      return;
    }
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Building name is required');
      return;
    }
    if (!formData.address.trim() || !formData.city.trim() || !formData.country.trim()) {
      toast.error('Street address, city and country are required');
      return;
    }

    setSubmitting(true);
    try {
      let finalFormData = formData;
      if (!formData.latitude || !formData.longitude) {
        try {
          const geo = await geocodeAddress(formData.address, formData.city, formData.country);
          finalFormData = { ...formData, latitude: String(geo.lat), longitude: String(geo.lng) };
          setFormData(finalFormData);
        } catch {
          /* geocoding failed — save without coordinates */
        }
      }

      const payload = buildPayload(finalFormData);
      const response = editing
        ? await updateBuilding(editing.id, payload)
        : await addBuilding(payload);
      if (!response.success) {
        throw new Error(response.error || `Failed to ${editing ? 'update' : 'add'} building`);
      }
      toast.success(editing ? 'Building updated' : 'Building added');
      setOpen(false);
      setEditing(null);
      setFormData(initialFormData);
    } catch (error) {
      toast.error(error.message || 'Building operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    open,
    editing,
    formData,
    submitting,
    openAdd,
    openEdit,
    close,
    handleInputChange,
    handleSubmit,
  };
};
